import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Typography,
  IconButton,
  Input,
  FormHelperText,
  Box,
} from "@mui/material";
import { Formik } from "formik";
import { ReactNode, useState } from "react";
import * as Yup from "yup";

import {
  GetTenDlcCampaignsForSubaccountQuery,
  UpdateFriendlyNameMutation,
  UpdateFriendlyNameMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  LIST_10DLC_CAMPAIGNS,
  UPDATE_FRIENDLY_NAME,
} from "~frontend/graphql/tenDlcCampaigns";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMutation from "~frontend/util/useMutation";

export const FriendlyName = ({
  campaign,
  isStaff,
}: {
  campaign: GetTenDlcCampaignsForSubaccountQuery["tenDlcCampaignsForSubaccount"][number];
  isStaff: boolean;
}): ReactNode => {
  const [isEditing, setIsEditing] = useState(false);
  const { subaccount } = useSubaccount()!;

  const [updateFriendlyName] = useMutation<
    UpdateFriendlyNameMutation,
    UpdateFriendlyNameMutationVariables
  >(UPDATE_FRIENDLY_NAME, {
    onErrorSnackbarMessage: "Error updating campaign name",
    refetchQueries: [LIST_10DLC_CAMPAIGNS],
  });

  return (
    <Typography variant="h2Bold" sx={{ marginBottom: 1 }}>
      {isStaff && isEditing ? (
        <Formik
          validationSchema={Yup.object().shape({
            friendlyName: Yup.string().required(),
          })}
          initialValues={{
            friendlyName:
              campaign.friendlyName || `Campaign ${campaign.tcrCampaignId}`,
          }}
          onSubmit={async ({ friendlyName }) => {
            await updateFriendlyName({
              variables: {
                input: {
                  tcrCampaignId: campaign.tcrCampaignId,
                  friendlyName,
                  subaccountId: subaccount.id,
                },
              },
            });
            setIsEditing(false);
          }}
        >
          {({ handleSubmit, errors, isSubmitting, handleChange, isValid }) => (
            <FlexBox>
              <FormHelperText error={true}>
                {errors.friendlyName}
              </FormHelperText>
              <Input
                type="text"
                id="friendlyName"
                name="friendlyName"
                disabled={isSubmitting}
                defaultValue={
                  campaign.friendlyName || `Campaign ${campaign.tcrCampaignId}`
                }
                onChange={handleChange}
                // Something in the CSS is making the input box slightly too short for the text; setting width avoids that
                sx={{ fontSize: "inherit", width: "20rem" }}
              />
              <IconButton
                disabled={!isStaff || isSubmitting || !isValid}
                onClick={() => handleSubmit()}
                aria-label="Save campaign name"
              >
                <SaveIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </FlexBox>
          )}
        </Formik>
      ) : (
        <FlexBox alignItems="center">
          <Box>
            {campaign.friendlyName || `Campaign ${campaign.tcrCampaignId}`}
          </Box>
          <IconButton
            size="small"
            onClick={() => setIsEditing(true)}
            disabled={!isStaff}
            sx={{ color: "inherit", display: isStaff ? "auto" : "none" }}
            aria-label="Edit campaign name"
          >
            <EditIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        </FlexBox>
      )}
    </Typography>
  );
};
